import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { C, E, RAYON, T, champ as champStyle } from '../theme';
import { API } from '../api';
import { MARQUE } from '../marque';
import { IconeCorbeille } from '../components/Icones';

// Repères communs à cette page (voir src/theme.js).
const carteSection = {
  background: C.surface, borderRadius: RAYON.lg, padding: E.lg,
  border: `1px solid ${C.bordure}`,
};
const titreSection = { fontSize: '14px', fontWeight: 600, color: C.texte,
                       margin: `0 0 ${E.lg}` };

function BanqueSearchSelect({ banques, value, onChange, onEmailChange, inputStyle }) {
  const sorted = [...banques].sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = sorted.find(b => String(b.id) === String(value));
  const filtered = sorted.filter(b => b.nom.toLowerCase().includes(query.toLowerCase()));

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery(''); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (b) => { onChange(String(b.id)); onEmailChange(b.email || ''); setQuery(''); setOpen(false); };
  const handleClear = (e) => { e.stopPropagation(); onChange(''); onEmailChange(''); setQuery(''); setOpen(false); };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input
          value={open ? query : (selected ? selected.nom : query)}
          onChange={e => { setQuery(e.target.value); onChange(''); onEmailChange(''); if (!open) setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(''); }}
          placeholder="Rechercher une banque partenaire…"
          style={{ ...inputStyle, paddingRight: selected ? '30px' : '14px' }}
        />
        {selected && (
          <button onMouseDown={handleClear}
            style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '13px', padding: 0 }}>✕</button>
        )}
      </div>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0, zIndex: 300, background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: '220px', overflowY: 'auto' }}>
          <div onMouseDown={() => { onChange(''); onEmailChange(''); setQuery(''); setOpen(false); }}
            style={{ padding: '8px 12px', fontSize: '13px', color: '#9ca3af', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
            onMouseLeave={e => e.currentTarget.style.background = 'white'}>
            — Aucune —
          </div>
          {filtered.length === 0
            ? <div style={{ padding: '8px 12px', fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>Aucun résultat</div>
            : filtered.map(b => (
              <div key={b.id} onMouseDown={() => handleSelect(b)}
                style={{ padding: '10px 12px', fontSize: '13px', color: '#1a2b4a', cursor: 'pointer', fontWeight: String(b.id) === String(value) ? '600' : '400', background: String(b.id) === String(value) ? '#eff6ff' : 'white' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = String(b.id) === String(value) ? '#eff6ff' : 'white'}>
                <div>{b.nom}</div>
                {b.email && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '1px' }}>✉️ {b.email}</div>}
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

function DossierFinancement({ user }) {
  const [prospects, setProspects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedProspectId, setSelectedProspectId] = useState(null);
  const [projetsProspect, setProjetsProspect] = useState([]);
  const searchRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const prospectIdFromUrl = params.get('prospect_id');
  const projetIdFromUrl = params.get('projet_id');
  const dossierIdFromUrl = params.get('dossier_id');
  const [dossierExistant, setDossierExistant] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erreurs, setErreurs] = useState([]);
  const [showEnvoiModal, setShowEnvoiModal] = useState(false);
  const [envoiDestinataires, setEnvoiDestinataires] = useState([{ nom: '', email: '' }]);
  const [envoiMessage, setEnvoiMessage] = useState('');
  const [envoiFichiers, setEnvoiFichiers] = useState([]);
  const [envoiLoading, setEnvoiLoading] = useState(false);
  const [envoiResultat, setEnvoiResultat] = useState(null);
  const [historiqueEnvois, setHistoriqueEnvois] = useState([]);
  const envoiFichierRef = useRef(null);
  const [suiviBanques, setSuiviBanques] = useState([]);
  const [editingSuivi, setEditingSuivi] = useState(null); // suivi_id | null
  const [suiviForm, setSuiviForm] = useState({ statut: 'accepte', date_reponse: '', commentaire: '' });
  const [banques, setBanques] = useState([]);
  const [utilisateurs, setUtilisateurs] = useState([]);
  const [gereParId, setGereParId] = useState(null);

  const [form, setForm] = useState({
    // Entreprise
    raison_sociale: '', date_creation: '', adresse: '', email: '',
    telephone: '', siret: '', code_naf: '', capital_social: '',
    // Dirigeants
    dirigeants: [{ nom: '', prenom: '', fonction: '', telephone: '', email: '' }],
    // Comptable
    comptable_adresse: '', comptable_telephone: '',
    // Pièces
    kbis: false, liasse_fiscale: false, cni: false, proforma: false, etat_parc: false,
    // Projet investissement
    type_financement: 'credit_bail',
    // Matériel
    materiels: [{ designation: '', prix_ht: '', neuf_occasion: 'Neuf', kilometrage: '', duree: '', vr: '' }],
    montant_total: '', pli_tresorerie: '', taux: '', periodicite: 'MENSUELLE', engagement_rachat: '',
    // Fournisseur
    fournisseur_nom: '', fournisseur_siret: '', garanties: '', livraison_type: 'en_stock', date_livraison: '',
    // Historique client
    historique_parc: '', nb_vehicules: '', marques_actuelles: '', anciennete_relation: '',
    // Entreprise info
    presentation: '', activite_clients: '', banque: '',
    point_financier: '', covid_pge: '', eco_projet: '', perspectives: '',
    // Motivation
    motivation: 'renouvellement',
    // Notes
    notes: '',
    // Organisme financier cible
    banque_cible_id: '', banque_cible_email: '',
  });

  useEffect(() => {
    axios.get(`${API}/prospects`).then(res => setProspects(res.data));
    axios.get(`${API}/banques`).then(res => setBanques(res.data)).catch(() => {});
    axios.get(`${API}/utilisateurs/attribuables`).then(res => setUtilisateurs(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchQuery.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await axios.get(`https://recherche-entreprises.api.gouv.fr/search?q=${encodeURIComponent(searchQuery)}&page=1&per_page=8`);
        if (response.data.results) { setSuggestions(response.data.results); setShowSuggestions(true); }
      } catch (e) { setSuggestions([]); }
      setSearchLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (selectedProspectId) {
      axios.get(`${API}/projets/${selectedProspectId}`)
        .then(res => setProjetsProspect(res.data))
        .catch(() => setProjetsProspect([]));
    } else {
      setProjetsProspect([]);
    }
  }, [selectedProspectId]);

  useEffect(() => {
    if (prospectIdFromUrl && prospects.length > 0) {
      const p = prospects.find(p => p.id === parseInt(prospectIdFromUrl));
      if (p) {
        setForm(f => ({
          ...f,
          raison_sociale: p.nom_societe || '',
          siret: p.siren || '',
          adresse: p.adresse || '',
          telephone: p.telephone || '',
          email: p.email || '',
          dirigeants: [{ nom: p.nom_dirigeant || '', prenom: '', fonction: '', telephone: p.telephone || '', email: p.email || '' }],
        }));
        setSearchQuery(p.nom_societe || '');
        setSelectedProspectId(parseInt(prospectIdFromUrl));
      }
    }
  }, [prospectIdFromUrl, prospects]);

  useEffect(() => {
    if (!projetIdFromUrl || projetsProspect.length === 0) return;
    const projet = projetsProspect.find(p => p.id === parseInt(projetIdFromUrl));
    if (!projet) return;
    const mapType = (t) => {
      if (t === 'crédit-bail') return 'credit_bail';
      if (t === 'location financière') return 'location_financiere';
      return 'achat';
    };
    setForm(f => ({
      ...f,
      type_financement: mapType(projet.type_financement),
      materiels: [{
        designation: projet.nom_produit || projet.materiel || '',
        prix_ht: projet.montant_financement != null ? String(projet.montant_financement) : '',
        neuf_occasion: projet.type_neuf_occasion || 'Neuf',
        kilometrage: '',
        duree: projet.duree_financement != null ? String(projet.duree_financement) : '',
        vr: projet.valeur_residuelle != null ? String(projet.valeur_residuelle) : '',
      }],
      montant_total: projet.montant_financement != null ? String(projet.montant_financement) : '',
      date_livraison: projet.date_livraison || '',
    }));
  }, [projetIdFromUrl, projetsProspect]);

  useEffect(() => {
    if (!dossierIdFromUrl) return;
    axios.get(`${API}/dossiers/${dossierIdFromUrl}`)
      .then(res => {
        const d = res.data;
        setDossierExistant(d);
        if (d.donnees) {
          try {
            const donnees = JSON.parse(d.donnees);
            setForm(f => ({ ...f, ...donnees }));
            if (d.raison_sociale) setSearchQuery(d.raison_sociale);
          } catch (_) {}
        }
        if (d.prospect_id) setSelectedProspectId(d.prospect_id);
        if (d.gere_par_id) setGereParId(d.gere_par_id);
        axios.get(`${API}/dossiers/${dossierIdFromUrl}/envois`)
          .then(r => setHistoriqueEnvois(r.data)).catch(() => {});
        axios.get(`${API}/dossiers/${dossierIdFromUrl}/suivi-banques`)
          .then(r => setSuiviBanques(r.data)).catch(() => {});
      })
      .catch(() => {});
  }, [dossierIdFromUrl]);

  useEffect(() => {
    const siret = (form.fournisseur_siret || '').replace(/[^0-9]/g, '');
    if (siret.length < 9) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/siren/${siret.slice(0, 9)}`);
        if (res.data.nom_societe) setForm(f => ({ ...f, fournisseur_nom: res.data.nom_societe }));
      } catch (_) {}
    }, 600);
    return () => clearTimeout(timer);
  }, [form.fournisseur_siret]);

  const selectEntreprise = async (e) => {
    const dirigeant = e.dirigeants?.[0];
    const nafCode = e.activite_principale || '';
    const nafLibelle = e.libelle_activite_principale || '';
    const codeNaf = nafCode && nafLibelle ? `${nafCode} — ${nafLibelle}` : nafCode;
    const dateCreation = e.date_creation
      ? new Date(e.date_creation).toLocaleDateString('fr-FR')
      : '';
    setForm({
      ...form,
      raison_sociale: e.nom_raison_sociale || '',
      siret: e.siege?.siret || e.siren || '',
      adresse: e.siege?.adresse || '',
      date_creation: dateCreation,
      code_naf: codeNaf,
      capital_social: e.capital_social != null ? String(e.capital_social) : '',
      dirigeants: dirigeant ? [{ nom: dirigeant.nom || '', prenom: dirigeant.prenom || '', fonction: '', telephone: '', email: '' }] : form.dirigeants,
    });
    setSearchQuery(e.nom_raison_sociale || '');
    setShowSuggestions(false);

    // Normalise un SIREN/SIRET : garde uniquement les chiffres puis prend les 9 premiers
    const normSiren = (s) => (s || '').replace(/[^0-9]/g, '').slice(0, 9);
    const normNom = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]/g, '');

    const sirenAPI = normSiren(e.siren);
    const nomAPI = normNom(e.nom_raison_sociale);

    // Re-fetch pour éviter un état périmé
    let liste = prospects;
    try {
      const res = await axios.get(`${API}/prospects`);
      liste = res.data;
      setProspects(liste);
    } catch (_) {}

    // 1. Correspondance SIREN stricte (priorité absolue)
    let matchCRM = sirenAPI ? liste.find(p => normSiren(p.siren) === sirenAPI) : null;

    // 2. Fallback nom uniquement si aucun match SIREN
    if (!matchCRM) {
      matchCRM = liste.find(p => {
        const nomCRM = normNom(p.nom_societe);
        return nomAPI.length >= 4 && nomCRM.length >= 4 && (nomAPI.includes(nomCRM) || nomCRM.includes(nomAPI));
      });
    }

    setSelectedProspectId(matchCRM ? matchCRM.id : null);
  };

  const addDirigeant = () => setForm({ ...form, dirigeants: [...form.dirigeants, { nom: '', prenom: '', fonction: '', telephone: '', email: '' }] });
  const removeDirigeant = (i) => setForm({ ...form, dirigeants: form.dirigeants.filter((_, idx) => idx !== i) });
  const updateDirigeant = (i, key, val) => {
    const d = [...form.dirigeants]; d[i][key] = val; setForm({ ...form, dirigeants: d });
  };

  const addMateriel = () => setForm({ ...form, materiels: [...form.materiels, { designation: '', prix_ht: '', neuf_occasion: 'Neuf', kilometrage: '', duree: '', vr: '' }] });
  const removeMateriel = (i) => setForm({ ...form, materiels: form.materiels.filter((_, idx) => idx !== i) });
  const updateMateriel = (i, key, val) => {
    const m = [...form.materiels]; m[i][key] = val; setForm({ ...form, materiels: m });
  };

  const valider = () => {
    const e = [];
    if (!form.raison_sociale) e.push('Raison sociale');
    if (!form.siret) e.push('SIRET/SIREN');
    if (!form.adresse) e.push('Adresse');
    if (!form.telephone) e.push('Téléphone');
    if (!form.email) e.push('Email');
    if (form.dirigeants[0].nom === '') e.push('Nom du dirigeant');
    if (!form.dirigeants[0].prenom) e.push('Prénom du dirigeant');
    if (!form.montant_total) e.push('Montant total');
    if (!form.fournisseur_nom) e.push('Nom du fournisseur');
    if (e.length > 0) { setErreurs(e); return false; }
    setErreurs([]);
    return true;
  };

  const sauvegarder = async () => {
    if (!dossierExistant) return;
    setSaving(true);
    try {
      await axios.put(`${API}/dossiers/${dossierExistant.id}`, {
        ...form,
        prospect_id: selectedProspectId || null,
        projet_id: projetIdFromUrl ? parseInt(projetIdFromUrl) : null,
        gere_par_id: gereParId || null,
      });
      setSaveMsg('success');
    } catch (_) {
      setSaveMsg('error');
    }
    setSaving(false);
    setTimeout(() => setSaveMsg(null), 3000);
  };

  const ouvrirEnvoiModal = () => {
    const banqueCible = banques.find(b => String(b.id) === String(form.banque_cible_id));
    setEnvoiDestinataires(banqueCible
      ? [{ nom: banqueCible.nom, email: banqueCible.email || '' }]
      : [{ nom: '', email: '' }]);
    setEnvoiMessage(`Bonjour,\n\nVeuillez trouver ci-joint le dossier de financement ${dossierExistant?.numero} concernant ${form.raison_sociale || dossierExistant?.raison_sociale}.\n\nCordialement,\n${MARQUE.nom}`);
    setEnvoiFichiers([]);
    setEnvoiResultat(null);
    setShowEnvoiModal(true);
  };

  const envoyerDossier = async () => {
    const dests = envoiDestinataires.filter(d => d.email.trim());
    if (!dests.length) return alert('Ajoutez au moins un destinataire avec un email');
    setEnvoiLoading(true);
    try {
      const fd = new FormData();
      fd.append('destinataires', JSON.stringify(dests));
      fd.append('message', envoiMessage);
      envoiFichiers.forEach(f => fd.append('fichiers', f));
      const res = await axios.post(`${API}/dossiers/${dossierExistant.id}/envoyer`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEnvoiResultat(res.data);
      const [envR, suiviR] = await Promise.all([
        axios.get(`${API}/dossiers/${dossierExistant.id}/envois`),
        axios.get(`${API}/dossiers/${dossierExistant.id}/suivi-banques`),
      ]);
      setHistoriqueEnvois(envR.data);
      setSuiviBanques(suiviR.data);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Erreur lors de l\'envoi';
      setEnvoiResultat({ erreur: detail });
    }
    setEnvoiLoading(false);
  };

  const genererPDF = async () => {
    if (!valider()) return;
    setLoading(true);
    try {
      const payload = {
        ...form,
        prospect_id: selectedProspectId || null,
        // Sans ce lien, le montant du dossier et celui du projet dont il est
        // issu s'additionnent au tableau de bord : la même affaire comptée
        // deux fois, sans que rien ne le signale.
        projet_id: projetIdFromUrl ? parseInt(projetIdFromUrl) : null,
        historique_financements: projetsProspect,
        _cree_par_id: user?.id || null,
        _gere_par_id: gereParId || null,
      };
      if (dossierExistant) {
        payload._dossier_id = dossierExistant.id;
      }
      const response = await axios.post(`${API}/dossier-financement-pdf`, payload, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'dossier_financement_cb_courtage.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      alert('Erreur lors de la génération du PDF');
    }
    setLoading(false);
  };

  const loyer_mensuel = (() => {
    const montant = parseFloat(form.montant_total) || 0;
    const pli = parseFloat(form.pli_tresorerie) || 0;
    const taux = parseFloat(form.taux) || 0;
    const duree = parseFloat(form.materiels[0]?.duree) || 0;
    if (!montant || !taux || !duree) return null;
    const montantNet = montant - pli;
    const tauxMensuel = taux / 100 / 12;
    if (tauxMensuel === 0) return null;
    return (montantNet * tauxMensuel / (1 - Math.pow(1 + tauxMensuel, -duree))).toFixed(2);
  })();

  const inputStyle = champStyle;
  const labelStyle = { display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500', color: '#6b7280' };
  const sectionStyle = { ...carteSection, marginBottom: E.lg };
  const h2Style = { color: '#1a2b4a', fontSize: '16px', fontWeight: '600', margin: '0 0 20px' };

  return (
    <div style={{ fontFamily: T.police, maxWidth: '900px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          {dossierExistant && (
            <button onClick={() => navigate(-1)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', fontSize: '14px', marginBottom: '8px', padding: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
              ← Retour
            </button>
          )}
          <h1 style={T.titre}>
            {dossierExistant ? `Dossier ${dossierExistant.numero}` : 'Dossier de financement'}
          </h1>
          <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>
            {dossierExistant ? `Créé le ${new Date(dossierExistant.date_creation).toLocaleDateString('fr-FR')} — ${dossierExistant.statut}` : 'À envoyer aux organismes financiers'}
          </p>
          {dossierExistant && (
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginTop: '6px', flexWrap: 'wrap' }}>
              {dossierExistant.cree_par_nom && (
                <span style={{ fontSize: '12px', color: '#6b7280' }}>Créé par <strong style={{ color: '#1a2b4a' }}>{dossierExistant.cree_par_nom}</strong>
                </span>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>Géré par</span>
                <select
                  value={gereParId || ''}
                  onChange={e => setGereParId(e.target.value ? parseInt(e.target.value) : null)}
                  style={{ padding: '3px 8px', borderRadius: '6px', border: '1px solid #e5e7eb', fontSize: '12px', color: '#1a2b4a', outline: 'none', background: 'white' }}>
                  <option value="">— Non assigné —</option>
                  {utilisateurs.filter(u => u.actif !== false).map(u => (
                    <option key={u.id} value={u.id}>{u.nom}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
        {dossierExistant && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {saveMsg === 'success' && <span style={{ fontSize: '13px', color: '#059669', fontWeight: '500' }}>Sauvegardé</span>}
            {saveMsg === 'error' && <span style={{ fontSize: '13px', color: '#ef4444', fontWeight: '500' }}>Erreur</span>}
            <button onClick={sauvegarder} disabled={saving}
              style={{ background: '#f0fdf4', color: '#15803d', border: '1px solid #86efac', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
              {saving ? 'Sauvegarde...' : '💾 Sauvegarder'}
            </button>
            <button onClick={ouvrirEnvoiModal}
              style={{ background: '#1a2b4a', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>Envoyer aux banques
            </button>
          </div>
        )}
      </div>

      {erreurs.length > 0 && (
        <div style={{ background: '#fff0f0', border: '1px solid #fecaca', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
          <p style={{ color: '#ef4444', fontWeight: '600', margin: '0 0 8px', fontSize: '14px' }}>Champs obligatoires manquants :</p>
          {erreurs.map((e, i) => <p key={i} style={{ color: '#ef4444', margin: '2px 0', fontSize: '13px' }}>• {e}</p>)}
        </div>
      )}

      {/* Recherche entreprise */}
      <div style={sectionStyle}>
        <h2 style={h2Style}>Recherche entreprise</h2>
        {(prospectIdFromUrl || dossierExistant) ? (
          <div style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', color: '#1a2b4a', fontWeight: '600' }}>{form.raison_sociale}
          </div>
        ) : (
          <div ref={searchRef} style={{ position: 'relative' }}>
            <input placeholder="Tapez le nom ou SIREN de l'entreprise..."
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              style={inputStyle} />
            {searchLoading && <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '12px' }}>...</span>}
            {showSuggestions && suggestions.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, background: 'white', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb', maxHeight: '250px', overflowY: 'auto', marginTop: '4px' }}>
                {suggestions.map((s, i) => (
                  <div key={i} onClick={() => selectEntreprise(s)}
                    style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f0f7ff'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                    <div style={{ fontWeight: '600', color: '#1a2b4a', fontSize: '13px' }}>{s.nom_raison_sociale}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>SIREN : {s.siren} — {s.siege?.code_postal} {s.siege?.libelle_commune}</div>
                    {(s.date_creation || s.activite_principale) && (
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '1px' }}>
                        {s.date_creation && `Créé le ${new Date(s.date_creation).toLocaleDateString('fr-FR')}`}
                        {s.date_creation && s.activite_principale && ' — '}
                        {s.activite_principale && `NAF ${s.activite_principale}`}
                        {s.libelle_activite_principale && ` (${s.libelle_activite_principale})`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {selectedProspectId && (
          <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#059669', fontWeight: '500' }}>
            <span style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '99px', padding: '3px 12px' }}>Client {MARQUE.nom} trouvé — {projetsProspect.length} financement(s) dans la base
            </span>
          </div>
        )}
      </div>

      {/* Historique des financements du cabinet */}
      {projetsProspect.length > 0 && (
        <div style={sectionStyle}>
          <h2 style={h2Style}>Historique des financements {MARQUE.nom}</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                {['Matériel / Produit', 'Type financement', 'Montant HT', 'Échéance'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {projetsProspect.map(p => (
                <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '11px 12px', fontSize: '13px', fontWeight: '500', color: '#1a2b4a' }}>{p.nom_produit || p.materiel || '—'}</td>
                  <td style={{ padding: '11px 12px', fontSize: '13px', color: '#374151' }}>{p.type_financement || '—'}</td>
                  <td style={{ padding: '11px 12px', fontSize: '13px', color: '#374151' }}>
                    {(p.montant_financement || p.prix_produit || p.valeur_ht)
                      ? Number(p.montant_financement || p.prix_produit || p.valeur_ht).toLocaleString('fr-FR') + ' €'
                      : '—'}
                  </td>
                  <td style={{ padding: '11px 12px', fontSize: '13px', color: '#374151' }}>
                    {p.date_echeance ? new Date(p.date_echeance).toLocaleDateString('fr-FR') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Entreprise */}
      <div style={sectionStyle}>
        <h2 style={h2Style}>Entreprise</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {[
            { key: 'raison_sociale', label: 'Raison sociale *' },
            { key: 'date_creation', label: 'Date de création' },
            { key: 'siret', label: 'SIRET / SIREN *' },
            { key: 'code_naf', label: 'Code NAF / APE' },
            { key: 'capital_social', label: 'Capital social (€)' },
            { key: 'telephone', label: 'Téléphone *' },
            { key: 'email', label: 'Adresse électronique *' },
          ].map(({ key, label }) => (
            <div key={key}>
              <label style={labelStyle}>{label}</label>
              <input value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} style={inputStyle} />
            </div>
          ))}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Adresse *</label>
            <input value={form.adresse} onChange={e => setForm({ ...form, adresse: e.target.value })} style={inputStyle} />
          </div>
        </div>
      </div>

      {/* Dirigeants */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ ...h2Style, margin: 0 }}>Dirigeant(s)</h2>
          <button onClick={addDirigeant} style={{ background: '#f0f7ff', color: '#1a2b4a', border: '1px solid #bfdbfe', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>+ Ajouter</button>
        </div>
        {form.dirigeants.map((d, i) => (
          <div key={i} style={{ border: '1px solid #f3f4f6', borderRadius: '8px', padding: '12px', marginBottom: '10px', background: '#fafafa' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '10px', marginBottom: '10px' }}>
              <div>
                <label style={labelStyle}>Nom *</label>
                <input value={d.nom} onChange={e => updateDirigeant(i, 'nom', e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Prénom</label>
                <input value={d.prenom} onChange={e => updateDirigeant(i, 'prenom', e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Fonction</label>
                <input value={d.fonction || ''} onChange={e => updateDirigeant(i, 'fonction', e.target.value)} placeholder="Ex : Dirigeant, Responsable avec pouvoir de signature…" style={inputStyle} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '10px', alignItems: 'end' }}>
              <div>
                <label style={labelStyle}>Téléphone</label>
                <input value={d.telephone} onChange={e => updateDirigeant(i, 'telephone', e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Email</label>
                <input value={d.email} onChange={e => updateDirigeant(i, 'email', e.target.value)} style={inputStyle} />
              </div>
              {i > 0 && (
                <button title="Supprimer" onClick={() => removeDirigeant(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px', paddingBottom: '8px' }}><IconeCorbeille taille={15} /></button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Comptable */}
      <div style={sectionStyle}>
        <h2 style={h2Style}>Coordonnées du comptable</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Adresse</label>
            <input value={form.comptable_adresse} onChange={e => setForm({ ...form, comptable_adresse: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Téléphone</label>
            <input value={form.comptable_telephone} onChange={e => setForm({ ...form, comptable_telephone: e.target.value })} style={inputStyle} />
          </div>
        </div>
      </div>

      {/* Pièces obligatoires */}
      <div style={sectionStyle}>
        <h2 style={h2Style}>Pièces obligatoires</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          {[
            { key: 'kbis', label: 'K-BIS de moins de 3 mois' },
            { key: 'liasse_fiscale', label: 'Liasse fiscale de moins de 18 mois' },
            { key: 'cni', label: 'CNI du dirigeant' },
            { key: 'proforma', label: 'Proforma, devis ou description du matériel HT' },
            { key: 'etat_parc', label: 'État de parc' },
          ].map(({ key, label }) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
              <input type="checkbox" checked={form[key]} onChange={e => setForm({ ...form, [key]: e.target.checked })}
                style={{ width: '16px', height: '16px', accentColor: '#1a2b4a' }} />
              {label}
            </label>
          ))}
        </div>
      </div>

      {/* Projet investissement */}
      <div style={sectionStyle}>
        <h2 style={h2Style}>Projet d'investissement</h2>
        <div style={{ display: 'flex', gap: '24px' }}>
          {[{ val: 'credit_bail', label: 'Crédit-bail' }, { val: 'location_financiere', label: 'Location financière' }, { val: 'enveloppe', label: 'Enveloppe' }].map(({ val, label }) => (
            <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
              <input type="radio" name="type_financement" value={val} checked={form.type_financement === val}
                onChange={e => setForm({ ...form, type_financement: e.target.value })}
                style={{ width: '16px', height: '16px', accentColor: '#1a2b4a' }} />
              {label}
            </label>
          ))}
        </div>
      </div>

      {/* Matériels */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ ...h2Style, margin: 0 }}>Matériel(s) à financer</h2>
          <button onClick={addMateriel} style={{ background: '#f0f7ff', color: '#1a2b4a', border: '1px solid #bfdbfe', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>+ Ajouter</button>
        </div>
        {form.materiels.map((m, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr auto', gap: '10px', marginBottom: '12px', alignItems: 'end' }}>
            {[
              { key: 'designation', label: 'Désignation' },
              { key: 'prix_ht', label: 'Prix HT' },
              { key: 'kilometrage', label: 'Kilométrage' },
              { key: 'duree', label: 'Durée (mois)' },
              { key: 'vr', label: 'V.R' },
            ].map(({ key, label }) => (
              <div key={key}>
                <label style={labelStyle}>{label}</label>
                <input value={m[key]} onChange={e => updateMateriel(i, key, e.target.value)} style={inputStyle} />
              </div>
            ))}
            <div>
              <label style={labelStyle}>Neuf/Occasion</label>
              <select value={m.neuf_occasion} onChange={e => updateMateriel(i, 'neuf_occasion', e.target.value)} style={inputStyle}>
                <option>Neuf</option>
                <option>Occasion</option>
              </select>
            </div>
            {i > 0 && <button title="Supprimer" onClick={() => removeMateriel(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px', paddingBottom: '8px' }}><IconeCorbeille taille={15} /></button>}
          </div>
        ))}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
          <div>
            <label style={labelStyle}>Montant total à financer HT *</label>
            <input value={form.montant_total} onChange={e => setForm({ ...form, montant_total: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Pli de trésorerie (€)</label>
            <input value={form.pli_tresorerie} onChange={e => setForm({ ...form, pli_tresorerie: e.target.value })} style={inputStyle} placeholder="0" />
          </div>
          <div>
            <label style={labelStyle}>Taux annuel (%)</label>
            <input value={form.taux} onChange={e => setForm({ ...form, taux: e.target.value })} style={inputStyle} placeholder="ex: 5.5" />
          </div>
        </div>
        {loyer_mensuel && (
          <div style={{ marginTop: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>Loyer mensuel estimé</span>
            <span style={{ fontSize: '22px', fontWeight: '700', color: '#059669' }}>
              {parseFloat(loyer_mensuel).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €<span style={{ fontSize: '14px', fontWeight: '400' }}>/mois</span>
            </span>
            <span style={{ fontSize: '12px', color: '#9ca3af' }}>
              Base : {((parseFloat(form.montant_total) || 0) - (parseFloat(form.pli_tresorerie) || 0)).toLocaleString('fr-FR')} € — Durée : {form.materiels[0]?.duree || '?'} mois — Taux : {form.taux}%
            </span>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
          <div>
            <label style={labelStyle}>Périodicité</label>
            <select value={form.periodicite} onChange={e => setForm({ ...form, periodicite: e.target.value })} style={inputStyle}>
              <option>MENSUELLE</option>
              <option>TRIMESTRIELLE</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Engagement de rachat signé par</label>
            <input value={form.engagement_rachat} onChange={e => setForm({ ...form, engagement_rachat: e.target.value })} style={inputStyle} />
          </div>
        </div>
      </div>

      {/* Fournisseur */}
      <div style={sectionStyle}>
        <h2 style={h2Style}>Fournisseur</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Nom *</label>
            <input value={form.fournisseur_nom} onChange={e => setForm({ ...form, fournisseur_nom: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>SIRET / SIREN <span style={{ color: '#9ca3af', fontWeight: '400' }}>(remplit le nom auto.)</span></label>
            <input value={form.fournisseur_siret} onChange={e => setForm({ ...form, fournisseur_siret: e.target.value })} style={inputStyle} placeholder="Saisir le SIRET/SIREN…" />
          </div>
          <div>
            <label style={labelStyle}>Livraison</label>
            <select value={form.livraison_type || 'en_stock'} onChange={e => setForm({ ...form, livraison_type: e.target.value, date_livraison: e.target.value === 'en_stock' ? '' : form.date_livraison })} style={inputStyle}>
              <option value="en_stock">En stock</option>
              <option value="date">Date de livraison</option>
            </select>
          </div>
          {(form.livraison_type === 'date') && (
            <div>
              <label style={labelStyle}>Date de livraison</label>
              <input type="date" value={form.date_livraison} onChange={e => setForm({ ...form, date_livraison: e.target.value })} style={inputStyle} />
            </div>
          )}
        </div>
      </div>

      {/* Organisme financier cible */}
      <div style={sectionStyle}>
        <h2 style={h2Style}>Organisme financier cible</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Banque partenaire</label>
            <BanqueSearchSelect
              banques={banques}
              value={form.banque_cible_id}
              onChange={val => setForm(f => ({ ...f, banque_cible_id: val }))}
              onEmailChange={email => setForm(f => ({ ...f, banque_cible_email: email }))}
              inputStyle={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Email de contact</label>
            <input
              type="email"
              value={form.banque_cible_email}
              onChange={e => setForm(f => ({ ...f, banque_cible_email: e.target.value }))}
              placeholder="Auto-rempli ou saisissez manuellement"
              style={inputStyle}
            />
          </div>
        </div>
        {form.banque_cible_id && (
          <div style={{ marginTop: '10px', fontSize: '12px', color: '#059669', background: '#f0fdf4', border: '1px solid #6ee7b7', borderRadius: '6px', padding: '8px 12px' }}>Banque sélectionnée — l'email sera pré-rempli dans le modal d'envoi
          </div>
        )}
      </div>

      {/* État de PARC */}
      <div style={sectionStyle}>
        <h2 style={h2Style}>État de PARC</h2>
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>État de PARC (ex : 22 porteurs, pièces dédiées...)</label>
          <textarea value={form.historique_parc} onChange={e => setForm({ ...form, historique_parc: e.target.value })}
            rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={labelStyle}>Nombre de véhicules actuels</label>
            <input value={form.nb_vehicules} onChange={e => setForm({ ...form, nb_vehicules: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Marques actuelles</label>
            <input value={form.marques_actuelles} onChange={e => setForm({ ...form, marques_actuelles: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Ancienneté de la relation client</label>
            <input type="date" value={form.anciennete_relation} onChange={e => setForm({ ...form, anciennete_relation: e.target.value })} style={inputStyle} />
          </div>
        </div>
      </div>

      {/* À propos de l'entreprise */}
      <div style={sectionStyle}>
        <h2 style={h2Style}>À propos de l'entreprise</h2>
        {[
          { key: 'presentation', label: '1. Présentation de l\'entreprise et/ou du groupe' },
          { key: 'activite_clients', label: '2. Activité / Clients' },
          { key: 'banque', label: '3. Informations importantes sur le bilan — Banque' },
          { key: 'point_financier', label: 'Point Financier' },
          { key: 'covid_pge', label: 'Covid / PGE / Reports' },
          { key: 'eco_projet', label: 'Éco de projet' },
          { key: 'perspectives', label: 'Perspectives / Projets' },
        ].map(({ key, label }) => (
          <div key={key} style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>{label}</label>
            <textarea value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })}
              rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
        ))}
      </div>

      {/* Motivation */}
      <div style={sectionStyle}>
        <h2 style={h2Style}>Motivation de l'investissement</h2>
        <div style={{ display: 'flex', gap: '24px' }}>
          {[{ val: 'renouvellement', label: 'Renouvellement' }, { val: 'agrandissement', label: 'Agrandissement' }].map(({ val, label }) => (
            <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
              <input type="radio" name="motivation" value={val} checked={form.motivation === val}
                onChange={e => setForm({ ...form, motivation: e.target.value })}
                style={{ width: '16px', height: '16px', accentColor: '#1a2b4a' }} />
              {label}
            </label>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div style={sectionStyle}>
        <h2 style={h2Style}>Notes</h2>
        <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
          rows={4} style={{ ...inputStyle, resize: 'vertical' }} />
      </div>

      <button onClick={genererPDF} disabled={loading}
        style={{ width: '100%', padding: '14px', background: '#1a2b4a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', cursor: 'pointer', fontWeight: '600', marginBottom: dossierExistant ? '20px' : '40px' }}>
        {loading ? 'Génération en cours...' : dossierExistant ? '📥 Régénérer le PDF (et sauvegarder)' : '📥 Générer le dossier de financement PDF'}
      </button>

      {/* Historique des envois */}
      {dossierExistant && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '40px' }}>
          <h2 style={titreSection}>Historique des envois
            {historiqueEnvois.length > 0 && <span style={{ color: '#9ca3af', fontSize: '13px', fontWeight: '400', marginLeft: '8px' }}>({historiqueEnvois.length})</span>}
          </h2>
          {historiqueEnvois.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '14px' }}>Aucun envoi effectué</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                  {['Banque', 'Email', 'Date', 'Statut'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {historiqueEnvois.map(e => (
                  <tr key={e.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px', fontSize: '13px', fontWeight: '600', color: '#1a2b4a' }}>{e.banque_nom}</td>
                    <td style={{ padding: '10px 12px', fontSize: '13px', color: '#6b7280' }}>{e.banque_email}</td>
                    <td style={{ padding: '10px 12px', fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' }}>{new Date(e.date_envoi).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {e.statut_envoi === 'success'
                        ? <span style={{ background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7', padding: '2px 8px', borderRadius: '99px', fontSize: '11px', fontWeight: '600' }}>Envoyé</span>
                        : <span style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', padding: '2px 8px', borderRadius: '99px', fontSize: '11px', fontWeight: '600' }} title={e.message_erreur}>Échec</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Suivi des réponses banques */}
      {dossierExistant && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '40px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ color: '#1a2b4a', fontSize: '16px', fontWeight: '600', margin: 0 }}>Suivi des réponses banques
              {suiviBanques.length > 0 && <span style={{ color: '#9ca3af', fontSize: '13px', fontWeight: '400', marginLeft: '8px' }}>({suiviBanques.length})</span>}
            </h2>
            {(() => {
              const dejaTrackees = new Set(suiviBanques.map(s => s.banque_email));
              const nouvelles = historiqueEnvois.filter(e => e.statut_envoi === 'success' && !dejaTrackees.has(e.banque_email));
              if (!nouvelles.length) return null;
              return (
                <button
                  onClick={() => Promise.all(nouvelles.map(e =>
                    axios.post(`${API}/dossiers/${dossierExistant.id}/suivi-banques`, {
                      banque_nom: e.banque_nom, banque_email: e.banque_email,
                      date_envoi: e.date_envoi,
                    })
                  )).then(() =>
                    axios.get(`${API}/dossiers/${dossierExistant.id}/suivi-banques`)
                      .then(r => setSuiviBanques(r.data))
                  )}
                  style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                  + Pré-remplir depuis envois ({nouvelles.length})
                </button>
              );
            })()}
          </div>

          {suiviBanques.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '14px' }}>
              Aucune banque suivie — envoyez le dossier pour démarrer le suivi automatiquement.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                  {['Banque', 'Email', 'Date envoi', 'Statut', 'Date réponse', 'Commentaire', ''].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {suiviBanques.map(s => {
                  const STATUT_STYLE = {
                    en_attente: { bg: '#fef3c7', text: '#92400e', border: '#fde68a', label: '⏳ En attente' },
                    accepte:    { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7', label: '✅ Accepté' },
                    refuse:     { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5', label: '❌ Refusé' },
                  };
                  const sc = STATUT_STYLE[s.statut] || STATUT_STYLE.en_attente;
                  const fmtDt = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

                  if (editingSuivi === s.id) {
                    return (
                      <tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6', background: '#f0f7ff' }}>
                        <td colSpan={7} style={{ padding: '12px 12px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr auto auto', gap: '8px', alignItems: 'center' }}>
                            <select value={suiviForm.statut} onChange={e => setSuiviForm(f => ({ ...f, statut: e.target.value }))}
                              style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #bfdbfe', fontSize: '13px', outline: 'none' }}>
                              <option value="accepte">Accepté</option>
                              <option value="refuse">Refusé</option>
                            </select>
                            <input type="date" value={suiviForm.date_reponse}
                              onChange={e => setSuiviForm(f => ({ ...f, date_reponse: e.target.value }))}
                              style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none' }} />
                            <input placeholder="Commentaire (optionnel)" value={suiviForm.commentaire}
                              onChange={e => setSuiviForm(f => ({ ...f, commentaire: e.target.value }))}
                              style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none' }} />
                            <button
                              onClick={() => axios.patch(
                                `${API}/dossiers/${dossierExistant.id}/suivi-banques/${s.id}`,
                                { statut: suiviForm.statut, date_reponse: suiviForm.date_reponse || null, commentaire: suiviForm.commentaire || null }
                              ).then(r => {
                                setSuiviBanques(prev => prev.map(x => x.id === s.id ? r.data : x));
                                setEditingSuivi(null);
                              })}
                              style={{ background: '#1a2b4a', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap' }}>Sauver
                            </button>
                            <button onClick={() => setEditingSuivi(null)}
                              style={{ background: 'white', color: '#6b7280', border: '1px solid #e5e7eb', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                              Annuler
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '10px 12px', fontSize: '13px', fontWeight: '600', color: '#1a2b4a' }}>{s.banque_nom}</td>
                      <td style={{ padding: '10px 12px', fontSize: '12px', color: '#6b7280' }}>{s.banque_email || '—'}</td>
                      <td style={{ padding: '10px 12px', fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDt(s.date_envoi)}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, padding: '2px 9px', borderRadius: '99px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                          {sc.label}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDt(s.date_reponse)}</td>
                      <td style={{ padding: '10px 12px', fontSize: '12px', color: '#374151', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.commentaire || '—'}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {s.statut === 'en_attente' && (
                            <button onClick={() => { setEditingSuivi(s.id); setSuiviForm({ statut: 'accepte', date_reponse: new Date().toISOString().slice(0, 10), commentaire: '' }); }}
                              style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '4px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap' }}>
                              Réponse →
                            </button>
                          )}
                          <button title="Supprimer" onClick={() => axios.delete(`${API}/dossiers/${dossierExistant.id}/suivi-banques/${s.id}`)
                            .then(() => setSuiviBanques(prev => prev.filter(x => x.id !== s.id)))}
                            style={{ background: 'none', border: '1px solid #fecaca', color: '#ef4444', padding: '4px 8px', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}><IconeCorbeille taille={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Modal envoi banques */}
      {showEnvoiModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '28px', width: '560px', maxWidth: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ color: '#1a2b4a', fontSize: '17px', fontWeight: '600', margin: 0 }}>Envoyer aux banques</h2>
              <button onClick={() => setShowEnvoiModal(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#9ca3af' }}>×</button>
            </div>

            {envoiResultat ? (
              <div>
                {envoiResultat.erreur ? (
                  <div style={{ background: '#fff0f0', border: '1px solid #fecaca', borderRadius: '10px', padding: '20px', textAlign: 'center' }}>
                    <div style={{ fontSize: '32px', marginBottom: '10px' }}>❌</div>
                    <div style={{ color: '#991b1b', fontWeight: '600', fontSize: '15px' }}>{envoiResultat.erreur}</div>
                  </div>
                ) : (
                  <div style={{ background: '#f0fdf4', border: '1px solid #6ee7b7', borderRadius: '10px', padding: '20px', textAlign: 'center', marginBottom: '16px' }}>
                    <div style={{ fontSize: '32px', marginBottom: '10px' }}>✅</div>
                    <div style={{ color: '#065f46', fontWeight: '700', fontSize: '16px' }}>Envoyé à {envoiResultat.envoyes} banque{envoiResultat.envoyes > 1 ? 's' : ''} sur {envoiResultat.total}</div>
                    {envoiResultat.detail?.filter(r => r.statut === 'error').map((r, i) => (
                      <div key={i} style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px' }}>{r.banque} ({r.email}) : {r.erreur}</div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button onClick={() => setEnvoiResultat(null)} style={{ background: 'white', color: '#374151', border: '1px solid #e5e7eb', padding: '9px 18px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>Renvoyer</button>
                  <button onClick={() => setShowEnvoiModal(false)} style={{ background: '#1a2b4a', color: 'white', border: 'none', padding: '9px 18px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>Fermer</button>
                </div>
              </div>
            ) : (
              <>
                {/* Destinataires */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Destinataires</label>
                    <button onClick={() => setEnvoiDestinataires(p => [...p, { nom: '', email: '' }])}
                      style={{ background: '#f0f7ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                      + Ajouter
                    </button>
                  </div>
                  {envoiDestinataires.map((d, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                      <input placeholder="Nom banque" value={d.nom}
                        onChange={e => setEnvoiDestinataires(p => p.map((x, j) => j === i ? { ...x, nom: e.target.value } : x))}
                        style={{ padding: '9px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none' }} />
                      <input placeholder="Email *" type="email" value={d.email}
                        onChange={e => setEnvoiDestinataires(p => p.map((x, j) => j === i ? { ...x, email: e.target.value } : x))}
                        style={{ padding: '9px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none' }} />
                      {envoiDestinataires.length > 1 && (
                        <button onClick={() => setEnvoiDestinataires(p => p.filter((_, j) => j !== i))}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '16px', padding: '4px' }}>✕</button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Message */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Message</label>
                  <textarea value={envoiMessage} onChange={e => setEnvoiMessage(e.target.value)} rows={5}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                </div>

                {/* Pièces jointes */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Pièces jointes</label>
                    <button onClick={() => envoiFichierRef.current?.click()}
                      style={{ background: '#f0f7ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                      + Ajouter
                    </button>
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Le PDF du dossier est joint automatiquement. Ajoutez RIB, bilans, K-bis, etc. si besoin.</div>
                  <input type="file" multiple ref={envoiFichierRef} accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx"
                    onChange={e => setEnvoiFichiers(prev => [...prev, ...Array.from(e.target.files)])}
                    style={{ display: 'none' }} />
                  {envoiFichiers.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {envoiFichiers.map((f, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '6px 10px' }}>
                          <span style={{ fontSize: '14px' }}>{f.name.toLowerCase().endsWith('.pdf') ? '📄' : f.name.match(/\.(doc|docx)$/i) ? '📝' : '📎'}</span>
                          <div style={{ flex: 1, fontSize: '12px', fontWeight: '500', color: '#1a2b4a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                          <span style={{ fontSize: '11px', color: '#9ca3af', flexShrink: 0 }}>{(f.size / 1024).toFixed(0)} Ko</span>
                          <button onClick={() => setEnvoiFichiers(prev => prev.filter((_, j) => j !== i))}
                            style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: '2px', flexShrink: 0 }}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {envoiFichiers.length === 0 && (
                    <div style={{ fontSize: '12px', color: '#d1d5db', fontStyle: 'italic' }}>Aucune pièce jointe ajoutée.</div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={envoyerDossier} disabled={envoiLoading}
                    style={{ flex: 1, background: '#1a2b4a', color: 'white', border: 'none', padding: '12px', borderRadius: '8px', cursor: envoiLoading ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600', opacity: envoiLoading ? 0.7 : 1 }}>
                    {envoiLoading ? 'Envoi en cours...' : `📧 Envoyer à ${envoiDestinataires.filter(d => d.email).length} banque${envoiDestinataires.filter(d => d.email).length > 1 ? 's' : ''}`}
                  </button>
                  <button onClick={() => setShowEnvoiModal(false)}
                    style={{ padding: '12px 20px', background: 'white', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    Annuler
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DossierFinancement;